import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  mineCount: 0,
  unAssignedCount: 0,
  allCount: 0,
  // Eltafouk: parallel slot fed by `getUnread` — exact same /meta endpoint
  // but with conversation_type=unread layered onto whatever scope (inbox,
  // team, label) is currently in play. Drives the "غير مقروء" pill badge
  // so it always reflects the same number the tab counters would land on
  // after a click; previously the badge was summed from a different API
  // (inboxes/unattended_counts) which doesn't filter by status=open, and
  // they drifted by 1-2 for non-open-but-unread conversations.
  unreadMineCount: 0,
  unreadUnAssignedCount: 0,
  unreadAllCount: 0,
  // Unread mentions for the current agent — drives the sidebar "Mentions" badge.
  // Independent of inbox/status; fed by the /meta endpoint and bumped in
  // real-time when a `conversation.mentioned` event arrives.
  mentionCount: 0,
  // display_ids of the conversations whose mention the agent hasn't opened yet
  // (from the mentions list response). Lets each row flag an unread mention
  // separately from the conversation's own unread-message state.
  unreadMentionIds: [],
};

export const getters = {
  getStats: $state => $state,
  getMentionCount: $state => $state.mentionCount,
  getUnreadMentionIds: $state => $state.unreadMentionIds,
};

// Create a debounced version of the actual API call function
const fetchMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_TAB_META, meta);
  } catch (error) {
    // ignore
  }
};

const fetchUnreadMetaData = async (commit, params) => {
  try {
    const response = await ConversationApi.meta({
      ...params,
      // Eltafouk: getUnread normally forces conversation_type=unread. But when the
      // caller passes an explicit `unread` flag (the all-comments badge scopes via
      // conversation_type='comments' + unread so it doesn't depend on the
      // not-yet-loaded comment-inbox-id list), keep that conversation_type so the
      // comment scope survives — the additive `unread` param does the narrowing.
      conversationType: params.unread ? params.conversationType : 'unread',
    });
    const {
      data: { meta },
    } = response;
    commit(types.SET_CONV_UNREAD_META, meta);
  } catch (error) {
    // ignore
  }
};

// Eltafouk: the sidebar's "Mentions" badge needs only the unread mention
// count, which the /meta endpoint returns regardless of scope. Fetching it via
// the generic `get` action wrote the global mine/unassigned/all counts into the
// SHARED stats store too, clobbering the mention-scoped tab counts the Mentions
// list had set — so on a refresh the tabs reverted to the whole-inbox totals.
// This path commits ONLY mention_count and leaves the tab counters untouched.
const fetchMentionCount = async commit => {
  try {
    const response = await ConversationApi.meta({});
    const {
      data: { meta },
    } = response;
    commit(types.SET_MENTION_COUNT, meta?.mention_count);
  } catch (error) {
    // ignore
  }
};

// Eltafouk: both /meta fetchers below keep their 500ms leading wait but get a
// 10000ms max-wait ceiling (was 1500ms). Rationale, measured on prod 2026-08-04:
// `helper/actionCable.js` re-emits `fetch_conversation_stats` from 8 websocket
// handlers, so under peak inbound traffic the events never leave a 500ms gap and
// the ceiling — not the wait — sets the rate. /conversations/meta was running
// 1,531 times in 3 minutes (~510/min, exactly ~13 agents x 60/1.5); together with
// /inboxes/unattended_counts that was 67% of ALL web requests on the box.
//
// Note the `get` tiers below pick a debounce by $state.allCount, so shrinking the
// open-conversation backlog moves clients into a FASTER tier — after 20,384 stale
// comment conversations were resolved that day, latency briefly got worse, not
// better. Raising this floor tier is what stops that from backfiring.
//
// The 500ms wait is deliberately unchanged: in quiet periods the badge still
// refreshes within half a second. The ceiling only binds mid-firehose, when the
// count is churning every second regardless.
const debouncedFetchMetaData = debounce(fetchMetaData, 500, false, 10000);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 5000, false, 10000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  10000,
  false,
  20000
);

// Not tiered by allCount like `get` is — this one always ran at the 1500ms
// ceiling regardless of backlog size, so it was the steadier of the two hammers.
const debouncedFetchUnreadMetaData = debounce(
  fetchUnreadMetaData,
  500,
  false,
  10000
);

export const actions = {
  get: async ({ commit, state: $state }, params) => {
    if ($state.allCount > 5000) {
      superLongDebouncedFetchMetaData(commit, params);
    } else if ($state.allCount > 100) {
      longDebouncedFetchMetaData(commit, params);
    } else {
      debouncedFetchMetaData(commit, params);
    }
  },
  // Eltafouk: companion to `get`. Always force conversation_type=unread
  // on the params so the badge's count source matches exactly what the
  // backend's unread filter would return — no semantic gap with the tab.
  getUnread: async ({ commit }, params) => {
    debouncedFetchUnreadMetaData(commit, params);
  },
  // Eltafouk: zero the unread badge slot synchronously. The unread* counts are
  // a single account-wide slot shared by every list view (DMs and Comments use
  // the same reused ChatList instance), so on navigation the previous view's
  // number would otherwise render under the new view until the re-scoped
  // `getUnread` (/meta) call resolves (≥500ms debounce) — the cross-section
  // "غير مقروء leaks between التعليقات and المحادثات" report. Clearing here means
  // the badge briefly hides (count 0 → v-if off) instead of showing another
  // view's count; the immediately-dispatched getUnread refills it for THIS view.
  resetUnread: ({ commit }) => {
    commit(types.SET_CONV_UNREAD_META, {
      mine_count: 0,
      unassigned_count: 0,
      all_count: 0,
    });
  },
  // Eltafouk: apply the agent's own read/unread toggle to the pill straight
  // away — see the ADJUST_CONV_UNREAD_META mutation for why waiting for /meta
  // is not good enough. `mine` / `unassigned` mirror the assignee tab the
  // conversation belongs to so switching tabs shows a consistent number.
  adjustUnread({ commit }, { delta, mine = false, unassigned = false } = {}) {
    if (!delta) return;
    commit(types.ADJUST_CONV_UNREAD_META, { delta, mine, unassigned });
  },
  set({ commit }, meta) {
    commit(types.SET_CONV_TAB_META, meta);
  },
  incrementMentionCount({ commit }) {
    commit(types.INCREMENT_MENTION_COUNT);
  },
  // Refresh only the sidebar's unread-mention badge without disturbing the
  // tab counters (which are owned by whichever conversation list is open).
  getMentionCount: async ({ commit }) => {
    fetchMentionCount(commit);
  },
  // Drop a conversation from the unread-mention list the moment the agent opens
  // it, so its row marker / unread filter update without waiting for a refetch.
  markMentionRead({ commit }, conversationId) {
    commit(types.REMOVE_UNREAD_MENTION_ID, conversationId);
  },
  // Flag a conversation as an unread mention when one arrives in real-time, so
  // the new row gets its marker / counts without a refetch.
  addUnreadMention({ commit }, conversationId) {
    commit(types.ADD_UNREAD_MENTION_ID, conversationId);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
      mention_count: mentionCount,
      unread_mention_ids: unreadMentionIds,
    } = {}
  ) {
    $state.mineCount = mineCount;
    $state.allCount = allCount;
    $state.unAssignedCount = unAssignedCount;
    // Only the /meta endpoint returns mention_count; guard so the index-meta
    // path (which omits it) doesn't clobber the badge count back to undefined.
    if (mentionCount !== undefined) {
      $state.mentionCount = mentionCount;
    }
    // Only the mentions list response carries unread_mention_ids; guard so
    // other views' meta don't wipe it.
    if (unreadMentionIds !== undefined) {
      $state.unreadMentionIds = unreadMentionIds;
    }
    $state.updatedOn = new Date();
  },
  [types.SET_CONV_UNREAD_META](
    $state,
    {
      mine_count: mineCount,
      unassigned_count: unAssignedCount,
      all_count: allCount,
    } = {}
  ) {
    $state.unreadMineCount = mineCount;
    $state.unreadAllCount = allCount;
    $state.unreadUnAssignedCount = unAssignedCount;
  },
  // Eltafouk: optimistic bump for the «غير مقروء» pill.
  //
  // The pill is only ever refreshed by /meta, and that refresh is deliberately
  // throttled: `debouncedFetchUnreadMetaData` waits 500ms but is emitted from
  // six websocket handlers on every inbound message, so during working hours no
  // 500ms gap ever occurs and the 10s max-wait ceiling sets the rate — on top of
  // a 5s Redis cache on the endpoint itself. The ceiling exists on purpose:
  // /meta was 67% of all requests on this box before it was raised.
  //
  // So an agent marking a conversation unread saw nothing happen for up to ~15s,
  // and by then other agents had READ several conversations, so the number
  // usually moved DOWN. Applying the agent's own +1 immediately costs zero
  // requests and keeps the throttle intact; the next /meta reconciles it.
  [types.ADJUST_CONV_UNREAD_META]($state, { delta, mine, unassigned } = {}) {
    const bump = (value, apply) =>
      apply ? Math.max(0, (value || 0) + delta) : value;
    $state.unreadAllCount = bump($state.unreadAllCount, true);
    $state.unreadMineCount = bump($state.unreadMineCount, mine);
    $state.unreadUnAssignedCount = bump(
      $state.unreadUnAssignedCount,
      unassigned
    );
  },
  [types.INCREMENT_MENTION_COUNT]($state) {
    $state.mentionCount += 1;
  },
  [types.SET_MENTION_COUNT]($state, mentionCount) {
    if (mentionCount !== undefined) {
      $state.mentionCount = mentionCount;
    }
  },
  [types.REMOVE_UNREAD_MENTION_ID]($state, conversationId) {
    $state.unreadMentionIds = $state.unreadMentionIds.filter(
      id => id !== conversationId
    );
  },
  [types.ADD_UNREAD_MENTION_ID]($state, conversationId) {
    if (!$state.unreadMentionIds.includes(conversationId)) {
      $state.unreadMentionIds = [...$state.unreadMentionIds, conversationId];
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
